import { BarChart3, BookOpen, CalendarDays, CalendarRange, CircleCheckBig, FileText, Images, Inbox, LayoutDashboard, LifeBuoy, Megaphone, Menu, Search, Send, Settings, type LucideIcon } from 'lucide-react';
import type { ComponentType } from 'react';
import { InboxNavBadge } from '../inbox/InboxNavBadge';
import { ReviewNavBadge } from '../review/ReviewNavBadge';
import type { PlaceholderKey } from './PlaceholderPage';

export type AreaKey = 'home' | 'review' | 'inbox' | 'calendar' | 'planning' | 'content' | 'campaigns' | 'analytics' | 'media' | 'knowledge' | 'settings' | 'help';

/**
 * Sidebar group (style guide 2.9a, S170-1): `main` holds the five plain main areas without a label,
 * `more` every other area under the label `nav.group.more`.
 */
export type NavGroupKey = 'main' | 'more';

export interface NavEntry {
  readonly key: AreaKey;
  readonly path: string;
  readonly icon: LucideIcon;
  /** Rendered after the label, e.g. the inbox's open count. */
  readonly badge?: ComponentType;
  /** Active only on exactly this path (the home page `/` would match every route otherwise). */
  readonly end?: boolean;
  /** Sidebar group; entries without one render standalone below a divider (settings). */
  readonly group?: NavGroupKey;
}

/** Sidebar group order (style guide 2.9a, S170-1). */
export const NAV_GROUP_ORDER: readonly NavGroupKey[] = ['main', 'more'];

/**
 * Primary navigation (spec 05 §6, S170-1): Start, Freigaben, Kalender, Beitraege and Ergebnisse,
 * then "Mehr" with every other area (the sidebar adds the search there); labels come from `nav.<key>`.
 */
export const PRIMARY_NAV: readonly NavEntry[] = [
  { key: 'home', path: '/', icon: LayoutDashboard, end: true, group: 'main' },
  { key: 'review', path: '/review', icon: CircleCheckBig, badge: ReviewNavBadge, group: 'main' },
  { key: 'calendar', path: '/calendar', icon: CalendarDays, group: 'main' },
  { key: 'content', path: '/content', icon: FileText, group: 'main' },
  { key: 'analytics', path: '/analytics', icon: BarChart3, group: 'main' },
  { key: 'planning', path: '/planning', icon: CalendarRange, group: 'more' },
  { key: 'campaigns', path: '/campaigns', icon: Megaphone, group: 'more' },
  { key: 'media', path: '/media', icon: Images, group: 'more' },
  { key: 'knowledge', path: '/knowledge', icon: BookOpen, group: 'more' },
  { key: 'inbox', path: '/inbox', icon: Inbox, badge: InboxNavBadge, group: 'more' },
  { key: 'settings', path: '/settings', icon: Settings },
  { key: 'help', path: '/help', icon: LifeBuoy },
];

/** Bottom tab bar entry below 1024 px (style guide 2.10, S150-1); labels come from `nav.tab.<key>`. */
export interface TabEntry {
  readonly key: 'home' | 'review' | 'calendar' | 'content' | 'more';
  readonly path: string;
  readonly icon: LucideIcon;
  /** Path prefixes that also mark this tab as current, e.g. the publications under "Beitraege". */
  readonly also?: readonly string[];
}

export const MORE_PATH = '/more';

/**
 * The five bottom tabs (S170-1): a sixth would not fit 375 px with 44 px targets, so Ergebnisse is
 * the first entry of "Mehr". "Mehr" is current on every route no other tab claims.
 */
export const TAB_NAV: readonly TabEntry[] = [
  { key: 'home', path: '/', icon: LayoutDashboard },
  { key: 'review', path: '/review', icon: CircleCheckBig },
  { key: 'calendar', path: '/calendar', icon: CalendarDays },
  { key: 'content', path: '/content', icon: FileText, also: ['/publications'] },
  { key: 'more', path: MORE_PATH, icon: Menu },
];

/** The tab that is current for `pathname`. */
export function currentTab(pathname: string): TabEntry['key'] {
  const claims = (prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`);
  const tab = TAB_NAV.find((entry) => entry.key !== 'more' && (entry.path === '/' ? pathname === '/' : [entry.path, ...(entry.also ?? [])].some(claims)));
  return tab?.key ?? 'more';
}

/** An entry of the "Mehr" page; labels from `nav.<key>`, the one-line explanation from `more.hint.<key>`. */
export interface MoreEntry {
  readonly key: AreaKey | 'search' | 'publications';
  readonly path: string;
  readonly icon: LucideIcon;
  readonly badge?: ComponentType;
}

/** Every area the bottom tabs do not carry, so nothing is lost on a phone (S150-1, S170-1: Ergebnisse first). */
export const MORE_NAV: readonly MoreEntry[] = [
  { key: 'analytics', path: '/analytics', icon: BarChart3 },
  { key: 'planning', path: '/planning', icon: CalendarRange },
  { key: 'inbox', path: '/inbox', icon: Inbox, badge: InboxNavBadge },
  { key: 'campaigns', path: '/campaigns', icon: Megaphone },
  { key: 'media', path: '/media', icon: Images },
  { key: 'knowledge', path: '/knowledge', icon: BookOpen },
  { key: 'publications', path: '/publications', icon: Send },
  { key: 'search', path: '/search', icon: Search },
  { key: 'settings', path: '/settings', icon: Settings },
  { key: 'help', path: '/help', icon: LifeBuoy },
];

/**
 * Areas whose feature has not arrived yet: they render the placeholder
 * (REQ-05-017). Every primary-nav area now has a real page (S60-2 shipped the
 * last one, the calendar); kept typed and empty, not removed, so the next new
 * area only needs an entry here.
 */
export const PLACEHOLDER_NAV: readonly (NavEntry & { key: PlaceholderKey })[] = [];
